import { apiFetch } from "./client";

/**
 * Sprint 7 (docs/api/sprint7-uloge-i-funkcije.md): roles are data the
 * administrator creates; every capability is a function `module:action`; a
 * user has roles plus per-user exceptions (grant / deny).
 */

/** A role as it appears on a user, an invitation or `/auth/me` — display only. */
export type RoleRef = {
  id: string;
  code: string;
  name: string;
  allFunctions?: boolean;
};

export type FunctionAction = "VIEW" | "CREATE" | "EDIT" | "DELETE" | (string & {});

export type FunctionView = {
  key: string;
  action: FunctionAction;
  name: string;
  description: string | null;
  /** Keys this function needs to be usable (sprint 7 §1.1); granting it brings them along. */
  requires: string[];
  /** A user's structure scope narrows where it applies (sprint 7 §12). */
  structureScoped: boolean;
};

export type FunctionModuleView = {
  module: string;
  moduleName: string;
  moduleDescription: string | null;
  sortOrder: number;
  functions: FunctionView[];
};

export type RoleView = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  allFunctions: boolean;
  system: boolean;
  sortOrder: number;
  memberCount: number;
  /** What the administrator ticked. */
  functionKeys: string[];
  /** What those need and therefore come with them (§1.1) — together with `functionKeys` what a member holds. */
  impliedKeys: string[];
  createdAt: string;
  updatedAt: string;
};

export type RoleRequest = {
  /** Ignored on PUT, but the contract requires it to be sent. */
  code: string;
  name: string;
  description: string | null;
  allFunctions: boolean;
  sortOrder: number;
  functionKeys: string[];
};

/**
 * Why a function is (or is not) held: `IMPLIED` = a held function needs it (`via` names them),
 * `DENY_IMPLIED` = it needs a denied function (`via` names the denied ones).
 */
export type EffectiveSource = "ROLE" | "GRANT" | "IMPLIED" | "DENY" | "DENY_IMPLIED" | "NONE";

export type EffectiveFunctionView = {
  key: string;
  granted: boolean;
  source: EffectiveSource;
  roleNames: string[];
  via: string[];
};

/** One branch of a structure scope (sprint 7 §12): the position with its path root → parent. */
export type ScopeBranchView = {
  id: string;
  code: string | null;
  name: string;
  kind: string;
  path: { id: string; name: string; code: string | null }[];
  /** The position was deleted; the branch stays until removed here. */
  deleted: boolean;
};

export type UserAccessView = {
  userId: string;
  roles: RoleRef[];
  grantKeys: string[];
  denyKeys: string[];
  effective: EffectiveFunctionView[];
  /** Empty = the whole structure. */
  structureScope: ScopeBranchView[];
};

export type UserAccessRequest = {
  roleIds: string[];
  grantKeys: string[];
  denyKeys: string[];
  /** Position ids; [] = the whole structure, left out = unchanged. */
  structureScope?: string[];
};

/** `^[A-Z][A-Z0-9_]{1,29}$` — same rule as the backend (`app_role.code`). */
export const ROLE_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,29}$/;

export const functionsApi = {
  catalogue: () => apiFetch<FunctionModuleView[]>("/admin/functions"),
};

export const rolesApi = {
  list: () => apiFetch<RoleView[]>("/admin/roles"),
  get: (id: string) => apiFetch<RoleView>(`/admin/roles/${encodeURIComponent(id)}`),
  create: (body: RoleRequest) => apiFetch<RoleView>("/admin/roles", { method: "POST", body }),
  update: (id: string, body: RoleRequest) =>
    apiFetch<RoleView>(`/admin/roles/${encodeURIComponent(id)}`, { method: "PUT", body }),
  remove: (id: string) => apiFetch<void>(`/admin/roles/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

export const userAccessApi = {
  get: (userId: string) => apiFetch<UserAccessView>(`/admin/users/${encodeURIComponent(userId)}/access`),
  update: (userId: string, body: UserAccessRequest) =>
    apiFetch<UserAccessView>(`/admin/users/${encodeURIComponent(userId)}/access`, { method: "PUT", body }),
};
